import type { User, UserRole, LoginCredentials } from '../types/auth';
import mockUsers from '../mock/users.json';
import {
  ensureSessionRefreshed,
  graphqlFetch,
  hasStoredSession,
  clearSessionFreshness,
  noteSessionFresh,
  noteSessionObserved,
  resetSessionRefreshBackoff,
} from './sessionRefresh';

const STORAGE_KEY_USER = 'nightlife_reporting_user';
const STORAGE_KEY_ROLE = 'nightlife_reporting_role';
const STORAGE_KEY_USER_ID = 'nightlife_reporting_user_id';

const GRAPHQL_URL = process.env.REACT_APP_CMS_GRAPHQL_URL || 'https://api-dev.nightlife.net/graphql';

// Users sign in on the Next.js site, which sets the auth_token cookie this app's
// GraphQL calls send. An expired session sends them back there.
const NEXTJS_LOGIN_URL =
  process.env.REACT_APP_NEXTJS_LOGIN_URL || `${process.env.REACT_APP_NEXTJS_URL || 'https://devnextjs.nightlife.net'}/login`;
const SESSION_EXPIRED_EVENT = 'nightlife:session-expired';

export type SessionCheck =
  | { status: 'valid'; user: User }
  | { status: 'expired' } // no session, or the token expired
  | { status: 'unreachable' }; // network/server error - the session state is unknown




// CMS role names, letters only and lowercase, per app role. Matched exactly,
// like the backend (payment/views.py) - a prefix would also admit e.g.
// "Administrative Assistant" or "Supervisee". Keep both lists the same.
const SUPER_ADMIN_ROLE_NAMES = ['superadmin', 'superadmins', 'superadministrator', 'superadministrators'];
const FULL_ADMIN_NAMES = ['admin', 'admins', 'administrator', 'administrators'];
const READ_ONLY_ADMIN_ROLE_NAMES = ['adminreadonly', 'readonlyadmin', 'administratorreadonly', 'readonlyadministrator'];
const ADMIN_ROLE_NAMES = [...FULL_ADMIN_NAMES, ...READ_ONLY_ADMIN_ROLE_NAMES];
const SUPERVISOR_ROLE_NAMES = ['supervisor', 'supervisors'];
const FULL_ADMIN_ROLE_NAMES = [...SUPER_ADMIN_ROLE_NAMES, ...FULL_ADMIN_NAMES, ...SUPERVISOR_ROLE_NAMES];

/** Letters only, lowercase: CMS role names vary ("Supervisor ", "super_visor", "Super Admin"). */
const normalizeRoleName = (name?: string) => (name || '').toLowerCase().replace(/[^a-z]/g, '');

export class AuthService {
  /**
   * Helper to map GraphQL roles array to standard UserRole.
   */
  private static mapGraphQLRoles(rolesList?: Array<{ id: number; name: string }>): UserRole {
    const names = new Set((rolesList || []).map((r) => normalizeRoleName(r.name)));
    const holds = (roles: string[]) => roles.some((r) => names.has(r));
    // Super admins get the Supervisor dashboard, even if they also hold "admin".
    if (holds(SUPER_ADMIN_ROLE_NAMES)) return 'Supervisor';
    if (holds(ADMIN_ROLE_NAMES)) return 'Admin';
    if (holds(SUPERVISOR_ROLE_NAMES)) return 'Supervisor';
    return 'Advertiser';
  }

  /**
   * Admin/Supervisor publish without paying - not read-only admin roles
   * ("adminreadonly"), which keep the Admin dashboard but pay to publish.
   * Same rule as the backend's has_full_admin_rights().
   */
  private static mapPaymentExempt(rolesList?: Array<{ id: number; name: string }>): boolean {
    return (rolesList || []).some((r) => FULL_ADMIN_ROLE_NAMES.includes(normalizeRoleName(r.name)));
  }

  /** Whether this user publishes without paying. Sessions saved before paymentExempt existed fall back to the role. */
  public static isPaymentExempt(user?: User | null): boolean {
    if (!user) return false;
    if (typeof user.paymentExempt === 'boolean') return user.paymentExempt;
    return user.role === 'Admin' || user.role === 'Supervisor';
  }

  /**
   * Validates credentials against GraphQL mutation Login via local proxy and persists session in cookies.
   */
  public static async login(credentials: LoginCredentials): Promise<User> {
    const loginQuery = `
      mutation Login($input: LoginInput!) {
        login(input: $input) {
          user {
            id
            email
            full_name
            roles {
              id
              name
            }
          }
          errors {
            message
          }
        }
      }
    `;

    try {
      const response = await fetch(GRAPHQL_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apollo-require-preflight': 'true',
        },
        credentials: 'include',
        body: JSON.stringify({
          query: loginQuery,
          variables: {
            input: {
              email: credentials.email,
              password: credentials.password,
            },
          },
        }),
      });

      if (!response.ok) {
        if (response.status >= 500) {
          throw new Error(`Server Error (${response.status}):Internal Server Error. Please try again later.`);
        }
        const text = await response.text().catch(() => '');
        throw new Error(`Authentication failed (${response.status}): ${text || response.statusText}`);
      }

      const result = await response.json();
      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }
      if (result.data?.login?.errors && result.data.login.errors.length > 0) {
        throw new Error(result.data.login.errors[0].message);
      }

      const gqlUser = result.data?.login?.user;
      if (gqlUser) {
        const userRole = AuthService.mapGraphQLRoles(gqlUser.roles);
        const authenticatedUser: User = {
          id: String(gqlUser.id),
          name: gqlUser.full_name || gqlUser.email,
          email: gqlUser.email,
          password: '',
          role: userRole,
          paymentExempt: AuthService.mapPaymentExempt(gqlUser.roles),
        };

        // Store user profile in local context (NO auth_token in localStorage)
        localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(authenticatedUser));
        localStorage.setItem(STORAGE_KEY_ROLE, userRole);
        localStorage.setItem(STORAGE_KEY_USER_ID, String(gqlUser.id));
        resetSessionRefreshBackoff();
        noteSessionFresh();

        return authenticatedUser;
      }
    } catch (err: any) {
      if (err.message && !err.message.includes('fetch') && !err.message.includes('Failed to fetch')) {
        throw err;
      }
      console.warn('[AuthService] GraphQL login network fallback active:', err);
    }

    // Dev Fallback against mockUsers if offline
    const foundUser = (mockUsers as User[]).find(
      (u) => u.email.toLowerCase() === credentials.email.toLowerCase()
    );

    if (!foundUser) {
      throw new Error('Invalid email or password');
    }

    if (credentials.password && credentials.password !== foundUser.password) {
      throw new Error('Invalid email or password');
    }

    localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(foundUser));
    localStorage.setItem(STORAGE_KEY_ROLE, foundUser.role);
    localStorage.setItem(STORAGE_KEY_USER_ID, foundUser.id);

    return foundUser;
  }

  /**
   * Asks GraphQL { me } who the cookie session belongs to (with the real role from
   * the CMS). Stores the user on success.
   *
   * `me` does not renew the access token and returns null both when logged out
   * and once the access token expires. Only a stored user is worth a refresh;
   * a transport failure or 429 stays unreachable so the user is not signed out.
   */
  public static async checkSession(): Promise<SessionCheck> {
    const first = await AuthService.queryMe();
    if (first.status !== 'expired' || !hasStoredSession()) return first;
    const refreshed = await ensureSessionRefreshed();
    if (refreshed === 'unavailable') return { status: 'unreachable' };
    if (refreshed !== 'refreshed') return first;
    return AuthService.queryMe();
  }

  private static async queryMe(): Promise<SessionCheck> {
    const meQuery = `
      query {
        me {
          id
          email
          full_name
          roles {
            id
            name
          }
        }
      }
    `;

    let response: Response;
    try {
      response = await graphqlFetch({ query: meQuery });
    } catch (e) {
      console.warn('[AuthService] Session check could not reach GraphQL:', e);
      return { status: 'unreachable' };
    }

    if (response.status === 401 || response.status === 403) return { status: 'expired' };
    if (!response.ok) return { status: 'unreachable' };

    try {
      const result = await response.json();
      const expiredAccess = Array.isArray(result.errors) && result.errors.some(
        (err: { extensions?: { code?: string }; message?: string }) =>
          err?.extensions?.code === 'ACCESS_TOKEN_EXPIRED' ||
          /session needs to be refreshed/i.test(err?.message || ''),
      );
      const gqlUser = result.data?.me;
      if (!gqlUser || expiredAccess) return { status: 'expired' };
      const userRole = AuthService.mapGraphQLRoles(gqlUser.roles);
      const authenticatedUser: User = {
        id: String(gqlUser.id),
        name: gqlUser.full_name || gqlUser.email,
        email: gqlUser.email,
        password: '',
        role: userRole,
        paymentExempt: AuthService.mapPaymentExempt(gqlUser.roles),
      };
      localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(authenticatedUser));
      localStorage.setItem(STORAGE_KEY_ROLE, userRole);
      localStorage.setItem(STORAGE_KEY_USER_ID, String(gqlUser.id));
      noteSessionObserved();
      return { status: 'valid', user: authenticatedUser };
    } catch (e) {
      console.warn('[AuthService] Session check returned an unreadable response:', e);
      return { status: 'unreachable' };
    }
  }

  /** Next.js login page that returns the user to `returnTo` after signing in. */
  public static getLoginUrl(returnTo: string = window.location.href): string {
    const sep = NEXTJS_LOGIN_URL.includes('?') ? '&' : '?';
    return `${NEXTJS_LOGIN_URL}${sep}redirect=${encodeURIComponent(returnTo)}`;
  }

  /** Tells the app the session expired (e.g. an API returned 401) - shows the login popup. */
  public static notifySessionExpired(): void {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }

  public static onSessionExpired(handler: () => void): () => void {
    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
  }

  /**
   * Queries GraphQL query { me } directly to verify active session from server.
   * If session is expired or invalid for Admin/Supervisor, clears local role and returns null.
   */
  public static async fetchCurrentUserFromGraphQL(): Promise<User | null> {
    const session = await AuthService.checkSession();
    if (session.status === 'valid') return session.user;
    // A blip or a 429 is not a dead session. Keep the stored user.
    if (session.status === 'unreachable') return AuthService.getStoredUser();

    const savedRole = localStorage.getItem(STORAGE_KEY_ROLE);
    if (savedRole === 'Admin' || savedRole === 'Supervisor') {
      AuthService.logout();
    }
    return null;
  }

  /**
   * Clears stored Admin/Supervisor role and session state.
   */
  public static logout(): void {
    localStorage.removeItem(STORAGE_KEY_USER);
    localStorage.removeItem(STORAGE_KEY_ROLE);
    localStorage.removeItem(STORAGE_KEY_USER_ID);
    // Leave a 429 / dead-session backoff in place so in-flight pages do not
    // keep calling refreshSession. The next successful login clears it.
    clearSessionFreshness();
  }

  /**
   * Resets active session explicitly to Advertiser (usr_001 / Alex Advertiser).
   */
  public static resetToAdvertiser(): User {
    const defaultUser = (mockUsers as User[])[0];
    localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(defaultUser));
    localStorage.setItem(STORAGE_KEY_ROLE, defaultUser.role);
    localStorage.setItem(STORAGE_KEY_USER_ID, defaultUser.id);
    return defaultUser;
  }

  /**
   * Returns current authenticated user from localStorage.
   */
  public static getCurrentUser(): User {
    return AuthService.getStoredUser() || AuthService.resetToAdvertiser();
  }

  /**
   * X-User-* headers for backend calls, from the logged-in user (the backend
   * prefers the auth_token cookie when the browser sends it).
   */
  public static getAuthHeaders(): Record<string, string> {
    const currentUser = AuthService.getCurrentUser();
    const headers: Record<string, string> = {};
    if (currentUser?.id) {
      headers['X-User-Id'] = currentUser.id;
      headers['X-User-Name'] = (currentUser as any).name || (currentUser as any).full_name || '';
      headers['X-User-Role'] = currentUser.role || 'Advertiser';
    }
    return headers;
  }

  /**
   * The user saved by the last login, or null - unlike getCurrentUser, never
   * falls back to the mock advertiser.
   */
  public static getStoredUser(): User | null {
    const savedUserStr = localStorage.getItem(STORAGE_KEY_USER);
    if (savedUserStr) {
      try {
        return JSON.parse(savedUserStr) as User;
      } catch (e) {
        console.error('Failed to parse user from localStorage:', e);
      }
    }
    return null;
  }

  /**
   * Returns all available mock users.
   */
  public static async getUsers(): Promise<User[]> {
    return mockUsers as User[];
  }
}
